import {inject, injectable} from "inversify";
import SERVICE_IDENTIFIER from "../constants/identifiers";
import Wallet from "./Wallet";
import IDOM from "../ui/IDOM";
import UIMessages from "../ui/messages/UIMessages";
import MadmaxMessage from "../ui/messages/MadmaxMessage";
import SystemConfig, {CONFIG_PARAMS} from "../system/SystemConfig";
import {MESSAGE_EVENTS, MESSAGE_TYPES} from "../error/errorMessages";
import IEventObserver from "../events/IEventObserver";
import events from "../events/EventsObservable";
import {GAME_EVENTS} from "../game/gameEvents";
import {pixiSetTimeoutAsync} from "../engine/pixi/pixiTimeout";
import Settings from "../settings/Settings";
import {EVENTS} from "../events/baseEvents";
import GameSlots from "../game/slot/GameSlots";
import {translate} from "../translations/translations";
import {BASE_ELEMENT_IDS} from "../ui/BaseDOM";
import {replaceParam} from "../helpers";
import * as calc from "./walletOperations"

@injectable()
export default class Freespins implements IEventObserver {

    private settings: Settings;
    private config: SystemConfig;
    private dom: IDOM;
    private uiMessages: UIMessages;
    private wallet: Wallet;
    private game: GameSlots;

    private type: string;

    constructor(
        @inject(SERVICE_IDENTIFIER.Settings) settings: Settings,
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.DOM) dom: IDOM,
        @inject(SERVICE_IDENTIFIER.UIMessages) uiMessages: UIMessages,
        @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
        @inject(SERVICE_IDENTIFIER.Game) game: GameSlots
    ) {
        this.settings = settings;
        this.config = config;
        this.dom = dom;
        this.uiMessages = uiMessages;
        this.wallet = wallet;
        this.game = game;

        this.type = this.settings.getCreditOrder();

        events.subscribe(MESSAGE_EVENTS.SWITCH_TO_REAL, this);
        events.subscribe(MESSAGE_EVENTS.SWITCH_TO_FREESPINS, this);
        events.subscribe(GAME_EVENTS.GAME_END_ROUND_RECALCED, this);
        events.subscribe(GAME_EVENTS.GAME_NEW_FREESPINS, this);
        events.subscribe(GAME_EVENTS.GAME_ALLOW_FREESPINS, this);
        events.subscribe(GAME_EVENTS.GAME_INVALID_FREESPINS, this);
        events.subscribe(GAME_EVENTS.GAME_END_ROUND, this);
    }

    async notify(eventName, data) {
        switch(eventName) {
            case MESSAGE_EVENTS.SWITCH_TO_FREESPINS:
                if (this.rejectWhileCycleOpen(eventName)) return;
                if (this.wallet.getFreeSpins() <= 0) {
                    events.dispatch(EVENTS.CLOSE_GAME);
                } else {
                    this.wallet.snapshotRealBet();
                    this.wallet.setIsBonus(true);
                    this.wallet.setBonusBet();
                    this.stopAutostart();
                    this.showFreespins();
                    await events.dispatch(GAME_EVENTS.GAME_FS_MODE_CHANGED, { isBonus: true });
                }
                break;
            case MESSAGE_EVENTS.SWITCH_TO_REAL:
                if (this.rejectWhileCycleOpen(eventName)) return;
                this.wallet.setIsBonus(false);
                this.wallet.restoreRealBet();
                this.stopAutostart();
                this.hideFreespins();
                await events.dispatch(GAME_EVENTS.GAME_FS_MODE_CHANGED, { isBonus: false });
                break;
            case GAME_EVENTS.GAME_END_ROUND:
                if (this.wallet.getFreeSpins() <= 0 && this.wallet.isBonus()) {
                    this.game.autostart?.stopAutostart();
                }
                break;
            case GAME_EVENTS.GAME_END_ROUND_RECALCED:
                if (
                    !this.game.freespins.isInFreeSpins() &&
                    this.wallet.getFreeSpins() <= 0 &&
                    this.wallet.isBonus()
                ) {
                    this.showContinueMessage();
                }
                break;
            case GAME_EVENTS.GAME_NEW_FREESPINS:
                if (this.wallet.isValidBonusBet()) {
                    if (this.wallet.isBonus()) {
                        this.showNewFreespinsMessage(data);
                    } else {
                        this.showNewFreespinsAndContinueMessage(data);
                    }
                }
                break;
            case GAME_EVENTS.GAME_ALLOW_FREESPINS:
                this.showOnStartMessage();
                break;
            case GAME_EVENTS.GAME_INVALID_FREESPINS:
                this.showFreespinsDataChanged();
                break;
        }
    }

    init() {
        if (this.type === "player_choice" && this.wallet.isValidBonusBet() && this.wallet.getFreeSpins() > 0) {
            this.showOnStartMessage();
        } else if (this.wallet.isValidBonusBet() && this.wallet.isBonus()) {
            this.showFreespins();
        }
    }

    public showContinueMessage() {
        const lang = this.config.get(CONFIG_PARAMS.LANGUAGE)
        let msg = new MadmaxMessage(
            "", 
            this.config.get(CONFIG_PARAMS.USE_FS_TOTAL_WIN) ? [
                translate(
                    "freespins_continue_total_win",
                    lang
                )+ `: ${calc.withCurrency(
                    Number(calc.getReal(Number(this.wallet.getFSTotalWin()),
                    this.wallet.getDecimalPlaces())),
                    // Number(this.wallet.getFSTotalWin()),
                    this.wallet.getCurrency(), 
                    this.wallet.getDecimalPlaces()
                )}`
            ,translate("freespins_continue", lang)]:translate("freespins_continue", lang),
            {
                yes: {
                    event: MESSAGE_EVENTS.SWITCH_TO_REAL,
                    msg: { "$translate": "freespins_continue_yes" }
                },
                no: {
                    event: MESSAGE_EVENTS.SWITCH_TO_FREESPINS,
                    msg: { "$translate": "freespins_continue_no" }
                }
            },
            lang,
            MESSAGE_TYPES.FREESPINS,
            100,
            this.config
        );
        msg.create();
        this.uiMessages.addMessage(msg);
        this.onShowMessage();
        msg.openMessage();
        msg.setOnClose(() => {
            msg.destroy();
        });
    }

    public showOnStartMessage() {
        let msg = new MadmaxMessage(
            "", translate("freespins_choose", this.config.get(CONFIG_PARAMS.LANGUAGE)),
            {
                yes: {
                    event: MESSAGE_EVENTS.SWITCH_TO_REAL,
                    msg: { "$translate": "freespins_choose_real_money" }
                },
                no: {
                    event: MESSAGE_EVENTS.SWITCH_TO_FREESPINS,
                    msg: { "$translate": "freespins_choose_freespins" }
                }
            },
            this.config.get(CONFIG_PARAMS.LANGUAGE),
            MESSAGE_TYPES.FREESPINS,
            100,
            this.config
        );
        msg.create();
        this.uiMessages.addMessage(msg);
        this.onShowMessage();
        msg.openMessage();
        msg.setOnClose(() => {
            msg.destroy();
        });
    }

    public showNewFreespinsMessage(data: any) {
        let txt = translate("freespins_receive", this.config.get(CONFIG_PARAMS.LANGUAGE));
        let msg = new MadmaxMessage(
            "", replaceParam(txt, 'freespins', data.rounds),
            {
                ok: {
                    event: MESSAGE_EVENTS.MESSAGE_CLOSE,
                    msg: { "$translate": "freespins_receive_ok" }
                }
            },
            this.config.get(CONFIG_PARAMS.LANGUAGE),
            MESSAGE_TYPES.FREESPINS,
            100,
            this.config
        );
        msg.create();
        this.uiMessages.addMessage(msg);
        this.onShowMessage();
        msg.openMessage();
        msg.setOnClose(() => {
            msg.destroy();
        });
    }

    public showNewFreespinsAndContinueMessage(data: any) {
        let txt = translate("freespins_receive_continue", this.config.get(CONFIG_PARAMS.LANGUAGE));
        let msg = new MadmaxMessage(
            "", replaceParam(txt, 'freespins', data.rounds),
            {
                yes: {
                    event: MESSAGE_EVENTS.SWITCH_TO_FREESPINS,
                    msg: { "$translate": "freespins_receive_continue_yes" }
                },
                no: {
                    event: MESSAGE_EVENTS.SWITCH_TO_REAL,
                    msg: { "$translate": "freespins_receive_continue_no" }
                }
            },
            this.config.get(CONFIG_PARAMS.LANGUAGE),
            MESSAGE_TYPES.FREESPINS,
            100,
            this.config
        );
        msg.create();
        this.uiMessages.addMessage(msg);
        this.onShowMessage();
        msg.openMessage();
        msg.setOnClose(() => {
            msg.destroy();
        });
    }

    public showFreespinsDataChanged() {
        let txt = translate("freespins_changed", this.config.get(CONFIG_PARAMS.LANGUAGE));
        txt = replaceParam(txt, 'bonus_bet', this.wallet.getBonusBet()/Math.pow(10,this.wallet.getDecimalPlaces()));
        let msg = new MadmaxMessage(
            "", txt,
            {
                yes: {
                    event: MESSAGE_EVENTS.SWITCH_TO_REAL,
                    msg: { "$translate": "freespins_changed_real_money" }
                },
                no: {
                    event: MESSAGE_EVENTS.MESSAGE_CLOSE_GAME,
                    msg: { "$translate": "freespins_changed_close" }
                }
            },
            this.config.get(CONFIG_PARAMS.LANGUAGE),
            MESSAGE_TYPES.FREESPINS,
            100,
            this.config
        );
        msg.create();
        this.uiMessages.addMessage(msg);
        this.onShowMessage();
        msg.openMessage();
        msg.setOnClose(() => {
            msg.destroy();
        });
    }

    public showFreespins() {
        this.dom.showFreeSpins();
        $('#'+BASE_ELEMENT_IDS.WRAPPER).addClass("freespins");
    }

    public hideFreespins() {
        this.dom.hideFreeSpins();
        $('#'+BASE_ELEMENT_IDS.WRAPPER).removeClass("freespins");
    }

    private rejectWhileCycleOpen(eventName: string): boolean {
        const roundRunning = this.game.round.isRunning();
        const inGameFreespins = this.game.isInFreeSpins();
        const open = roundRunning || inGameFreespins;
        if (open) {
            console.warn(`Freespins: ignoring ${eventName} while a cycle is open`, { roundRunning, inGameFreespins });
        }
        return open;
    }

    private stopAutostart() {
        this.game.autostart?.stopAutostart();
        this.game.autostart?.resetLastAutostart();
    }

    private onShowMessage() {
        this.game.setForcespin(false);
        this.game.autostart?.stopAutostart();
    }
}